// src/components/books/modals/MessageConfigModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  Plus, 
  Minus, 
  BookOpen, 
  Target, 
  Award, 
  MessageSquare,
  Settings,
  Info,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { firestoreService } from '../../../services/firestoreService';

export const MessageConfigModal = ({ 
  isOpen, 
  onClose, 
  message, 
  bookCategory = 'education',
  onSave 
}) => {
  const [loading, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('metadata');
  const [errors, setErrors] = useState({});
  
  // Metadata state
  const [metadata, setMetadata] = useState({
    topics: [],
    subtopics: [],
    tags: [],
    learningObjectives: [],
    knowledgeLevel: 'understand',
    difficulty: 'medium',
    estimatedTime: 5,
    prerequisites: []
  });
  
  // Scoring state
  const [scoring, setScoring] = useState({
    enabled: false,
    type: 'question',
    maxPoints: 10,
    passingScore: 6,
    scoringMethod: 'exact',
    correctAnswer: {
      value: '',
      acceptableVariations: [],
      partialCreditRules: []
    },
    optionScoring: [],
    rubric: {
      criteria: []
    }
  });
  
  // Assessment state
  const [assessment, setAssessment] = useState({
    isGraded: true,
    autoGrade: true,
    showCorrectAnswer: 'after_submission',
    allowMultipleAttempts: false,
    maxAttempts: 1,
    timeLimit: null,
    feedback: {
      immediate: '',
      incorrect: '',
      partial: '',
      hints: []
    }
  });
  
  // Analytics state
  const [analytics, setAnalytics] = useState({
    trackingEnabled: true,
    trackTime: true,
    trackAttempts: true,
    trackInteractions: ['focus', 'blur', 'input'],
    learningAnalytics: {
      conceptsAssessed: [],
      skillsRequired: []
    }
  });

  useEffect(() => {
    if (isOpen && message) {
      // Initialize with existing data or defaults
      setMetadata({
        topics: message.metadata?.topics || [],
        subtopics: message.metadata?.subtopics || [],
        tags: message.metadata?.tags || [],
        learningObjectives: message.metadata?.learningObjectives || [],
        knowledgeLevel: message.metadata?.knowledgeLevel || 'understand',
        difficulty: message.metadata?.difficulty || 'medium',
        estimatedTime: message.metadata?.estimatedTime || 5,
        prerequisites: message.metadata?.prerequisites || []
      });
      
      setScoring({
        enabled: message.scoring?.enabled || false,
        type: message.scoring?.type || getDefaultScoringType(message.userResponseType),
        maxPoints: message.scoring?.maxPoints || 10,
        passingScore: message.scoring?.passingScore || 6,
        scoringMethod: message.scoring?.scoringMethod || 'exact',
        correctAnswer: {
          value: message.scoring?.correctAnswer?.value || '',
          acceptableVariations: message.scoring?.correctAnswer?.acceptableVariations || [],
          partialCreditRules: message.scoring?.correctAnswer?.partialCreditRules || []
        },
        optionScoring: message.scoring?.optionScoring || getDefaultOptionScoring(message.userResponseType),
        rubric: message.scoring?.rubric || { criteria: [] }
      });
      
      setAssessment({
        isGraded: message.assessment?.isGraded ?? true,
        autoGrade: message.assessment?.autoGrade ?? true,
        showCorrectAnswer: message.assessment?.showCorrectAnswer || 'after_submission',
        allowMultipleAttempts: message.assessment?.allowMultipleAttempts || false,
        maxAttempts: message.assessment?.maxAttempts || 1,
        timeLimit: message.assessment?.timeLimit || null,
        feedback: {
          immediate: message.assessment?.feedback?.immediate || '',
          incorrect: message.assessment?.feedback?.incorrect || '',
          partial: message.assessment?.feedback?.partial || '',
          hints: message.assessment?.feedback?.hints || []
        }
      });
      
      setAnalytics({
        trackingEnabled: message.analytics?.trackingEnabled ?? true,
        trackTime: message.analytics?.trackTime ?? true,
        trackAttempts: message.analytics?.trackAttempts ?? true,
        trackInteractions: message.analytics?.trackInteractions || ['focus', 'blur', 'input'],
        learningAnalytics: {
          conceptsAssessed: message.analytics?.learningAnalytics?.conceptsAssessed || [],
          skillsRequired: message.analytics?.learningAnalytics?.skillsRequired || []
        }
      });
    }
  }, [isOpen, message]);

  const getDefaultScoringType = (userResponseType) => {
    if (!userResponseType) return 'question';
    const type = userResponseType.split('(')[0];
    const typeMap = {
      'multiselect': 'option',
      'choice': 'option', 
      'rating': 'feedback',
      'text': 'rubric',
      'textarea': 'rubric'
    };
    return typeMap[type] || 'question';
  };

  const getDefaultOptionScoring = (userResponseType) => {
    if (!userResponseType || !userResponseType.includes('options:')) return [];
    
    const optionsMatch = userResponseType.match(/options:([^,]+(?:,[^:]+)*?)(?=,\w+:|$)/);
    if (!optionsMatch) return [];
    
    const options = optionsMatch[1].split(',').map(opt => opt.trim());
    return options.map(option => ({
      option: option,
      points: 1,
      correct: false
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (scoring.enabled) {
      if (scoring.type === 'question' && !scoring.correctAnswer.value) {
        newErrors.correctAnswer = 'Correct answer is required when scoring is enabled';
      }
      if (scoring.type === 'option' && scoring.optionScoring.length === 0) {
        newErrors.optionScoring = 'Option scoring is required for choice/multiselect questions';
      }
      if (scoring.maxPoints <= 0) {
        newErrors.maxPoints = 'Max points must be greater than 0';
      }
    }
    
    if (metadata.topics.length === 0 && bookCategory === 'education') {
      newErrors.topics = 'At least one topic is required for educational content';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      // Use the REAL Firestore document ID
      const firestoreDocId = message.firestoreDocId; // ← "IMYU7JSWjBRhMK9xK7Mh"
      
      console.log('Real Firestore document ID:', firestoreDocId);
      console.log('Document data id field:', message.id);         // "szYmaCUVebUwyIbn6m1i" 
      console.log('MessageId field:', message.messageId);         // "msg_1756312498795"
      
      if (!firestoreDocId) {
        console.warn('No Firestore document ID found');
        onSave?.({ metadata, scoring, assessment, analytics });
        onClose();
        return;
      }

      // This will now use the correct document ID: "IMYU7JSWjBRhMK9xK7Mh"
      await firestoreService.updateMessageConfiguration(firestoreDocId, {
        metadata,
        scoring,
        assessment,
        analytics
      });
      
      onSave?.({ metadata, scoring, assessment, analytics });
      onClose();
    } catch (error) {
      console.error('Error saving message configuration:', error);
      setErrors({ save: 'Failed to save configuration. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'metadata', label: 'Metadata', icon: BookOpen },
    { id: 'scoring', label: 'Scoring', icon: Award },
    { id: 'assessment', label: 'Assessment', icon: Target },
    { id: 'analytics', label: 'Analytics', icon: Settings }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Configure Message</h2>
            <p className="text-sm text-gray-500 mt-1">
              {message?.message?.substring(0, 100)}...
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === 'metadata' && (
            <MetadataTab 
              metadata={metadata} 
              setMetadata={setMetadata} 
              bookCategory={bookCategory}
              errors={errors}
            />
          )}
          
          {activeTab === 'scoring' && (
            <ScoringTab 
              scoring={scoring} 
              setScoring={setScoring}
              message={message}
              errors={errors}
            />
          )}
          
          {activeTab === 'assessment' && (
            <AssessmentTab 
              assessment={assessment} 
              setAssessment={setAssessment}
              errors={errors}
            />
          )}
          
          {activeTab === 'analytics' && (
            <AnalyticsTab 
              analytics={analytics} 
              setAnalytics={setAnalytics}
              errors={errors}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            {Object.keys(errors).length > 0 && (
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">Please fix errors before saving</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {loading ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Metadata Tab Component
const MetadataTab = ({ metadata, setMetadata, bookCategory, errors }) => {
  const addArrayItem = (field, value) => {
    if (value && !metadata[field].includes(value)) {
      setMetadata(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
    }
  };

  const removeArrayItem = (field, index) => {
    setMetadata(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const ArrayInput = ({ field, label, placeholder }) => {
    const [inputValue, setInputValue] = useState('');

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addArrayItem(field, inputValue);
                setInputValue('');
              }
            }}
          />
          <button
            onClick={() => {
              addArrayItem(field, inputValue);
              setInputValue('');
            }}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        
        {metadata[field].length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {metadata[field].map((item, index) => (
              <span 
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
              >
                {item}
                <button
                  onClick={() => removeArrayItem(field, index)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        
        {errors[field] && (
          <p className="text-red-500 text-xs mt-1">{errors[field]}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Topics */}
        <ArrayInput 
          field="topics" 
          label="Topics" 
          placeholder="Add topic..."
        />
        
        {/* Subtopics */}
        <ArrayInput 
          field="subtopics" 
          label="Subtopics" 
          placeholder="Add subtopic..."
        />
        
        {/* Tags */}
        <ArrayInput 
          field="tags" 
          label="Tags" 
          placeholder="Add tag..."
        />
        
        {/* Learning Objectives */}
        <ArrayInput 
          field="learningObjectives" 
          label="Learning Objectives" 
          placeholder="Add learning objective..."
        />
        
        {/* Knowledge Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Knowledge Level (Bloom's Taxonomy)
          </label>
          <select
            value={metadata.knowledgeLevel}
            onChange={(e) => setMetadata(prev => ({ ...prev, knowledgeLevel: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="remember">Remember</option>
            <option value="understand">Understand</option>
            <option value="apply">Apply</option>
            <option value="analyze">Analyze</option>
            <option value="evaluate">Evaluate</option>
            <option value="create">Create</option>
          </select>
        </div>
        
        {/* Difficulty */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
          <select
            value={metadata.difficulty}
            onChange={(e) => setMetadata(prev => ({ ...prev, difficulty: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>
        
        {/* Estimated Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estimated Time (minutes)
          </label>
          <input
            type="number"
            min="1"
            max="60"
            value={metadata.estimatedTime}
            onChange={(e) => setMetadata(prev => ({ ...prev, estimatedTime: parseInt(e.target.value) || 5 }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        {/* Prerequisites */}
        <ArrayInput 
          field="prerequisites" 
          label="Prerequisites" 
          placeholder="Add prerequisite..."
        />
      </div>
    </div>
  );
};

// Scoring Tab Component
const ScoringTab = ({ scoring, setScoring, message, errors }) => {
  const userResponseType = message?.userResponseType || '';
  const responseType = userResponseType.split('(')[0];

  const updateCorrectAnswer = (field, value) => {
    setScoring(prev => ({
      ...prev,
      correctAnswer: {
        ...prev.correctAnswer,
        [field]: value
      }
    }));
  };

  const addVariation = (variation) => {
    if (variation && !scoring.correctAnswer.acceptableVariations.includes(variation)) {
      updateCorrectAnswer('acceptableVariations', [
        ...scoring.correctAnswer.acceptableVariations,
        variation
      ]);
    }
  };

  const removeVariation = (index) => {
    updateCorrectAnswer('acceptableVariations', 
      scoring.correctAnswer.acceptableVariations.filter((_, i) => i !== index)
    );
  };

  const addPartialCreditRule = () => {
    updateCorrectAnswer('partialCreditRules', [
      ...scoring.correctAnswer.partialCreditRules,
      { pattern: '', points: 0, feedback: '' }
    ]);
  };

  const updatePartialCreditRule = (index, field, value) => {
    const updated = [...scoring.correctAnswer.partialCreditRules];
    updated[index] = { ...updated[index], [field]: value };
    updateCorrectAnswer('partialCreditRules', updated);
  };

  const removePartialCreditRule = (index) => {
    updateCorrectAnswer('partialCreditRules',
      scoring.correctAnswer.partialCreditRules.filter((_, i) => i !== index)
    );
  };

  const updateOptionScoring = (index, field, value) => {
    const updated = [...scoring.optionScoring];
    updated[index] = { ...updated[index], [field]: value };
    setScoring(prev => ({ ...prev, optionScoring: updated }));
  };

  return (
    <div className="space-y-6">
      {/* Enable Scoring */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="enableScoring"
          checked={scoring.enabled}
          onChange={(e) => setScoring(prev => ({ ...prev, enabled: e.target.checked }))}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor="enableScoring" className="text-sm font-medium text-gray-700">
          Enable scoring for this question
        </label>
      </div>

      {scoring.enabled && (
        <div className="space-y-6">
          {/* Basic Scoring Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Points</label>
              <input
                type="number"
                min="1"
                value={scoring.maxPoints}
                onChange={(e) => setScoring(prev => ({ ...prev, maxPoints: parseInt(e.target.value) || 10 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.maxPoints && <p className="text-red-500 text-xs mt-1">{errors.maxPoints}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Passing Score</label>
              <input
                type="number"
                min="0"
                max={scoring.maxPoints}
                value={scoring.passingScore}
                onChange={(e) => setScoring(prev => ({ ...prev, passingScore: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Scoring Method</label>
              <select
                value={scoring.scoringMethod}
                onChange={(e) => setScoring(prev => ({ ...prev, scoringMethod: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="exact">Exact Match</option>
                <option value="partial">Partial Credit</option>
                <option value="weighted">Weighted</option>
                <option value="rubric">Rubric-based</option>
              </select>
            </div>
          </div>

          {/* Question-based Scoring */}
          {(scoring.type === 'question' || responseType === 'math' || responseType === 'text') && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Correct Answer Configuration</h3>
              
              {/* Correct Answer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Correct Answer</label>
                <input
                  type="text"
                  value={scoring.correctAnswer.value}
                  onChange={(e) => updateCorrectAnswer('value', e.target.value)}
                  placeholder="Enter the correct answer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.correctAnswer && <p className="text-red-500 text-xs mt-1">{errors.correctAnswer}</p>}
              </div>

              {/* Acceptable Variations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Acceptable Variations</label>
                <AcceptableVariationsList 
                  variations={scoring.correctAnswer.acceptableVariations}
                  onAdd={addVariation}
                  onRemove={removeVariation}
                />
              </div>

              {/* Partial Credit Rules */}
              {scoring.scoringMethod === 'partial' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Partial Credit Rules</label>
                    <button
                      onClick={addPartialCreditRule}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors flex items-center gap-1"
                    >
                      <Plus className="h-3 w-3" />
                      Add Rule
                    </button>
                  </div>
                  
                  {scoring.correctAnswer.partialCreditRules.map((rule, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 mb-2">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                        <input
                          type="text"
                          placeholder="Regex pattern"
                          value={rule.pattern}
                          onChange={(e) => updatePartialCreditRule(index, 'pattern', e.target.value)}
                          className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="number"
                          placeholder="Points"
                          value={rule.points}
                          onChange={(e) => updatePartialCreditRule(index, 'points', parseInt(e.target.value) || 0)}
                          className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <div className="flex gap-1">
                          <input
                            type="text"
                            placeholder="Feedback"
                            value={rule.feedback}
                            onChange={(e) => updatePartialCreditRule(index, 'feedback', e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <button
                            onClick={() => removePartialCreditRule(index)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Option-based Scoring */}
          {(scoring.type === 'option' || responseType === 'choice' || responseType === 'multiselect') && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Option Scoring Configuration</h3>
              
              {scoring.optionScoring.map((option, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Option</label>
                      <span className="text-sm text-gray-900">{option.option}</span>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Points</label>
                      <input
                        type="number"
                        value={option.points}
                        onChange={(e) => updateOptionScoring(index, 'points', parseInt(e.target.value) || 0)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={option.correct}
                        onChange={(e) => updateOptionScoring(index, 'correct', e.target.checked)}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <label className="text-sm text-gray-700">Correct</label>
                    </div>
                  </div>
                </div>
              ))}
              
              {errors.optionScoring && <p className="text-red-500 text-xs">{errors.optionScoring}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Assessment Tab Component
const AssessmentTab = ({ assessment, setAssessment, errors }) => {
  const addHint = (hint) => {
    if (hint && !assessment.feedback.hints.includes(hint)) {
      setAssessment(prev => ({
        ...prev,
        feedback: {
          ...prev.feedback,
          hints: [...prev.feedback.hints, hint]
        }
      }));
    }
  };

  const removeHint = (index) => {
    setAssessment(prev => ({
      ...prev,
      feedback: {
        ...prev.feedback,
        hints: prev.feedback.hints.filter((_, i) => i !== index)
      }
    }));
  };

  return (
    <div className="space-y-6">
      {/* Grading Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Grading Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isGraded"
              checked={assessment.isGraded}
              onChange={(e) => setAssessment(prev => ({ ...prev, isGraded: e.target.checked }))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="isGraded" className="text-sm font-medium text-gray-700">
              This question is graded
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="autoGrade"
              checked={assessment.autoGrade}
              onChange={(e) => setAssessment(prev => ({ ...prev, autoGrade: e.target.checked }))}
              disabled={!assessment.isGraded}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="autoGrade" className="text-sm font-medium text-gray-700">
              Enable automatic grading
            </label>
          </div>
        </div>
      </div>

      {/* Answer Reveal Settings */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Show Correct Answer
        </label>
        <select
          value={assessment.showCorrectAnswer}
          onChange={(e) => setAssessment(prev => ({ ...prev, showCorrectAnswer: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="never">Never</option>
          <option value="immediately">Immediately after answer</option>
          <option value="after_submission">After submission</option>
          <option value="after_deadline">After deadline</option>
        </select>
      </div>

      {/* Attempts Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="allowMultipleAttempts"
            checked={assessment.allowMultipleAttempts}
            onChange={(e) => setAssessment(prev => ({ ...prev, allowMultipleAttempts: e.target.checked }))}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="allowMultipleAttempts" className="text-sm font-medium text-gray-700">
            Allow multiple attempts
          </label>
        </div>

        {assessment.allowMultipleAttempts && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Max Attempts</label>
            <input
              type="number"
              min="1"
              value={assessment.maxAttempts}
              onChange={(e) => setAssessment(prev => ({ ...prev, maxAttempts: parseInt(e.target.value) || 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}
      </div>

      {/* Time Limit */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Time Limit (seconds, optional)
        </label>
        <input
          type="number"
          min="0"
          value={assessment.timeLimit || ''}
          onChange={(e) => setAssessment(prev => ({ ...prev, timeLimit: e.target.value ? parseInt(e.target.value) : null }))}
          placeholder="No time limit"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Feedback Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Feedback Configuration</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Immediate Feedback (correct answer)
            </label>
            <textarea
              value={assessment.feedback.immediate}
              onChange={(e) => setAssessment(prev => ({
                ...prev,
                feedback: { ...prev.feedback, immediate: e.target.value }
              }))}
              placeholder="Feedback when answer is correct"
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Incorrect Feedback
            </label>
            <textarea
              value={assessment.feedback.incorrect}
              onChange={(e) => setAssessment(prev => ({
                ...prev,
                feedback: { ...prev.feedback, incorrect: e.target.value }
              }))}
              placeholder="Feedback when answer is incorrect"
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Partial Credit Feedback
            </label>
            <textarea
              value={assessment.feedback.partial}
              onChange={(e) => setAssessment(prev => ({
                ...prev,
                feedback: { ...prev.feedback, partial: e.target.value }
              }))}
              placeholder="Feedback for partial credit"
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Hints */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Hints</label>
          <HintsList 
            hints={assessment.feedback.hints}
            onAdd={addHint}
            onRemove={removeHint}
          />
        </div>
      </div>
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab = ({ analytics, setAnalytics }) => {
  const toggleInteraction = (interaction) => {
    setAnalytics(prev => ({
      ...prev,
      trackInteractions: prev.trackInteractions.includes(interaction)
        ? prev.trackInteractions.filter(i => i !== interaction)
        : [...prev.trackInteractions, interaction]
    }));
  };

  const addConcept = (concept) => {
    if (concept && !analytics.learningAnalytics.conceptsAssessed.includes(concept)) {
      setAnalytics(prev => ({
        ...prev,
        learningAnalytics: {
          ...prev.learningAnalytics,
          conceptsAssessed: [...prev.learningAnalytics.conceptsAssessed, concept]
        }
      }));
    }
  };

  const removeConcept = (index) => {
    setAnalytics(prev => ({
      ...prev,
      learningAnalytics: {
        ...prev.learningAnalytics,
        conceptsAssessed: prev.learningAnalytics.conceptsAssessed.filter((_, i) => i !== index)
      }
    }));
  };

  const addSkill = (skill) => {
    if (skill && !analytics.learningAnalytics.skillsRequired.includes(skill)) {
      setAnalytics(prev => ({
        ...prev,
        learningAnalytics: {
          ...prev.learningAnalytics,
          skillsRequired: [...prev.learningAnalytics.skillsRequired, skill]
        }
      }));
    }
  };

  const removeSkill = (index) => {
    setAnalytics(prev => ({
      ...prev,
      learningAnalytics: {
        ...prev.learningAnalytics,
        skillsRequired: prev.learningAnalytics.skillsRequired.filter((_, i) => i !== index)
      }
    }));
  };

  return (
    <div className="space-y-6">
      {/* Basic Analytics */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Basic Analytics</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="trackingEnabled"
              checked={analytics.trackingEnabled}
              onChange={(e) => setAnalytics(prev => ({ ...prev, trackingEnabled: e.target.checked }))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="trackingEnabled" className="text-sm font-medium text-gray-700">
              Enable analytics tracking
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="trackTime"
              checked={analytics.trackTime}
              onChange={(e) => setAnalytics(prev => ({ ...prev, trackTime: e.target.checked }))}
              disabled={!analytics.trackingEnabled}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="trackTime" className="text-sm font-medium text-gray-700">
              Track time spent
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="trackAttempts"
              checked={analytics.trackAttempts}
              onChange={(e) => setAnalytics(prev => ({ ...prev, trackAttempts: e.target.checked }))}
              disabled={!analytics.trackingEnabled}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="trackAttempts" className="text-sm font-medium text-gray-700">
              Track attempts
            </label>
          </div>
        </div>
      </div>

      {/* Interaction Tracking */}
      {analytics.trackingEnabled && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Track Interactions</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['focus', 'blur', 'input', 'hints_used', 'keyboard_used'].map(interaction => (
              <label key={interaction} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={analytics.trackInteractions.includes(interaction)}
                  onChange={() => toggleInteraction(interaction)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 capitalize">
                  {interaction.replace('_', ' ')}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Learning Analytics */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Learning Analytics</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Concepts Assessed */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Concepts Assessed</label>
            <ArrayInputWithList 
              items={analytics.learningAnalytics.conceptsAssessed}
              onAdd={addConcept}
              onRemove={removeConcept}
              placeholder="Add concept..."
            />
          </div>

          {/* Skills Required */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Skills Required</label>
            <ArrayInputWithList 
              items={analytics.learningAnalytics.skillsRequired}
              onAdd={addSkill}
              onRemove={removeSkill}
              placeholder="Add skill..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const AcceptableVariationsList = ({ variations, onAdd, onRemove }) => {
  const [inputValue, setInputValue] = useState('');

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Add acceptable variation..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAdd(inputValue);
              setInputValue('');
            }
          }}
        />
        <button
          onClick={() => {
            onAdd(inputValue);
            setInputValue('');
          }}
          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      
      {variations.length > 0 && (
        <div className="space-y-1">
          {variations.map((variation, index) => (
            <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
              <span className="text-sm text-gray-900">{variation}</span>
              <button
                onClick={() => onRemove(index)}
                className="text-red-600 hover:text-red-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HintsList = ({ hints, onAdd, onRemove }) => {
  const [inputValue, setInputValue] = useState('');

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Add hint..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAdd(inputValue);
              setInputValue('');
            }
          }}
        />
        <button
          onClick={() => {
            onAdd(inputValue);
            setInputValue('');
          }}
          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      
      {hints.length > 0 && (
        <div className="space-y-1">
          {hints.map((hint, index) => (
            <div key={index} className="flex items-start justify-between bg-gray-50 px-3 py-2 rounded">
              <span className="text-sm text-gray-900 flex-1">{hint}</span>
              <button
                onClick={() => onRemove(index)}
                className="text-red-600 hover:text-red-800 ml-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ArrayInputWithList = ({ items, onAdd, onRemove, placeholder }) => {
  const [inputValue, setInputValue] = useState('');

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAdd(inputValue);
              setInputValue('');
            }
          }}
        />
        <button
          onClick={() => {
            onAdd(inputValue);
            setInputValue('');
          }}
          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <span 
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
            >
              {item}
              <button
                onClick={() => onRemove(index)}
                className="text-blue-600 hover:text-blue-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MessageConfigModal;